import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TenantContextValue {
  /**
   * Slug del tenant extraído del primer segmento de la URL.
   * Ejemplo: /acme/dashboard → tenantSlug = "acme"
   * Es null fuera de rutas de tenant (login, admin, etc.).
   */
  tenantSlug: string | null;

  /**
   * Construye una ruta prefijada con el slug actual.
   * Uso: buildPath('rrhh/employees') → "/acme/rrhh/employees"
   */
  buildPath: (path: string) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
// Debe estar dentro del árbol de React Router (BrowserRouter > Routes)
// para que useParams() encuentre el parámetro :tenantSlug.

export function TenantProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ tenantSlug?: string }>();
  const tenantSlug = params.tenantSlug ?? null;

  const value = useMemo<TenantContextValue>(
    () => ({
      tenantSlug,
      buildPath: (path: string) =>
        tenantSlug ? `/${tenantSlug}/${path.replace(/^\//, '')}` : `/${path}`,
    }),
    [tenantSlug],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('[useTenant] debe usarse dentro de <TenantProvider>');
  }
  return ctx;
}
